import { AxiosError } from 'axios';
import api from '../utils/api';
import { Lookup, APIErrorResponse } from '../types/lookup';

const PUBLIC_URL = '/api/lookups/public';
const ADMIN_URL = '/api/lookups/admin';

// Create type for new lookup request
export interface CreateLookupRequest {
  category: string;
  value: string;
  displayName: string;
  sessionRate?: number;
  numberOfSessions?: number;
}

// Fetch lookups for a specific category (protected)
export const fetchLookups = async (category: string): Promise<Lookup[]> => {
  try {
    const response = await api.get<Lookup[]>(`${ADMIN_URL}/${category}`);
    if (!Array.isArray(response.data)) {
      console.error('Invalid lookup data format:', response.data);
      return [];
    }
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    console.error('Error fetching lookups:', error);
    throw new Error(axiosError.response?.data?.error || 'Failed to fetch lookups');
  }
};

// Add a new lookup
export const addLookup = async (lookup: CreateLookupRequest): Promise<Lookup> => {
  try {
    const response = await api.post<Lookup>(ADMIN_URL, lookup);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    throw new Error(axiosError.response?.data?.error || 'Failed to create lookup');
  }
};

// Toggle lookup active status
export const toggleLookupStatus = async (value: string, active: boolean): Promise<Lookup> => {
  try {
    const response = await api.patch<Lookup>(`${ADMIN_URL}/${value}`, { active });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    throw new Error(axiosError.response?.data?.error || 'Failed to update lookup');
  }
};

// Update lookup details
export const updateLookup = async (lookup: Lookup): Promise<Lookup> => {
  try {
    const response = await api.put(`${ADMIN_URL}/${lookup._id}`, {
      value: lookup.value,
      displayName: lookup.displayName,
      sessionRate: lookup.sessionRate,
      numberOfSessions: lookup.numberOfSessions,
      sortOrder: lookup.sortOrder,
      active: lookup.active
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lookup:', error);
    throw error;
  }
};

// Fetch all public lookups
export const fetchAllPublicLookups = async (): Promise<{
  availability: Lookup[];
  sessionTypes: Lookup[];
  sessionRates: Lookup[];
  modality: Lookup[];
  [key: string]: Lookup[];
}> => {
  try {
    // Fetch all required lookup categories in parallel
    const [availability, sessionTypes, sessionRates, modality] = await Promise.all([
      api.get<Lookup[]>(`${PUBLIC_URL}/availability`),
      api.get<Lookup[]>(`${PUBLIC_URL}/sessionTypes`),
      api.get<Lookup[]>(`${PUBLIC_URL}/sessionRates`),
      api.get<Lookup[]>(`${PUBLIC_URL}/modality`)
    ]);

    return {
      availability: availability.data || [],
      sessionTypes: sessionTypes.data || [],
      sessionRates: sessionRates.data || [],
      modality: modality.data || []
    };
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    console.error('Error fetching lookups:', error);
    throw new Error(axiosError.response?.data?.error || 'Failed to fetch lookups');
  }
};

// Fetch public lookups for a specific category
export const fetchPublicLookups = async (category: string): Promise<Lookup[]> => {
  try {
    const response = await api.get<Lookup[]>(`${PUBLIC_URL}/${category}`);
    return response.data || [];
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    console.error('Error fetching public lookups:', error);
    throw new Error(axiosError.response?.data?.error || 'Failed to fetch lookups');
  }
};